import React from 'react';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material';
import useAnalytics from '@/hooks/use-analytics';
import { decodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';

interface BaseSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  fireAnalytics?: boolean;
}

export const BaseSwitch: React.FC<BaseSwitchProps> = ({
  checked,
  onChange,
  disabled,
  id,
  name,
  inputRef,
  fireAnalytics = true,
}) => {
  const { trackEvent } = useAnalytics();

  return (
    <StyledSwitch
      id={id}
      checked={checked}
      onChange={(e) => {
        if (fireAnalytics) {
          trackEvent(`${decodeFieldKey(id ?? 'UNKNOWN_SWITCH').attribute}_SWITCHED_${checked ? 'OFF' : 'ON'}`);
        }
        onChange(e.target.checked);
      }}
      disabled={disabled}
      name={name}
      inputRef={inputRef}
    />
  );
};

const StyledSwitch = styled(Switch)`
  --focus-ring-width: 4px;
  --width: calc(40px + var(--focus-ring-width) * 2);
  --height: calc(20px + var(--focus-ring-width) * 2);
  --thumb-size: calc(var(--height) - 2px - var(--focus-ring-width) * 2);

  width: var(--width);
  height: var(--height);
  padding: var(--focus-ring-width);
  transition: transform 200ms var(--ease-out-quart);

  &:has(.MuiSwitch-switchBase.Mui-focusVisible) {
    outline: var(--focus-ring-width) solid color-mix(in srgb, var(--mui-palette-secondary-main) 25%, transparent);
    outline-offset: calc(-1 * var(--focus-ring-width));
    border-radius: 999px;
  }

  .MuiSwitch-switchBase {
    top: var(--focus-ring-width);
    left: var(--focus-ring-width);
    padding: 1px;
    color: var(--mui-palette-background-main);

    &.Mui-checked {
      transform: translateX(20px);
      color: var(--mui-palette-background-main);

      & + .MuiSwitch-track {
        background-color: var(--mui-palette-secondary-main);
        opacity: 1;
      }
    }

    &.Mui-disabled {
      & + .MuiSwitch-track {
        opacity: 0.5;
      }
    }
  }

  .MuiSwitch-thumb {
    width: var(--thumb-size);
    height: var(--thumb-size);
    box-shadow: 0 2px 2px hsl(0 0% 0% / 0.25);
    color: var(--mui-palette-background-main);
  }

  .MuiTouchRipple-root {
    display: none;
  }

  .MuiSwitch-track {
    border-radius: 999px;
    background-color: var(--mui-palette-border-main);
    opacity: 1;
    transition: background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }
`;
